import * as csc from 'cornerstone-core';
// @ts-ignore
import * as csm from 'cornerstone-math';
import * as cst from 'cornerstone-tools';
import * as _ from 'lodash';
import { projectAlongAxis } from 'app/CornerstoneTools/pixelToCanvasUtils';
import {
  getLineIntersection,
  getLinesAngle,
  rotateVector,
} from 'app/CornerstoneTools/geometryHelper';
import toStringDegree from 'app/CornerstoneTools/geometry/toStringDegree';
import {
  BaseAnnotationTool,
  getNewContext,
  draw,
  drawHandles,
  drawLine,
  drawTextBox,
  path,
  getHandleNearImagePoint,
  lineSegDistance,
  cursors,
} from 'app/CornerstoneTools';
import ChainingToolBuilder from 'app/CornerstoneTools/ChainingToolBuilder';
import {
  EventWithCornerstoneDetail,
  Point,
  ExternalAngleAnnotation,
} from 'app/CornerstoneTools/types';

const CORNERSTONE_TEXT_PADDING = 5;
const LINE_PROJECTION_COLOR = 'rgba(255,255,255,0.6)';

export type ExternalAngleToolConfiguration = cst.BaseToolConfiguration & {
  drawHandles: boolean;
  drawHandlesOnHover: boolean;
  hideHandlesIfMoving: boolean;
  renderDashed: boolean;
};

function getFarthestFromIntersection(
  intersectionPoint: Point,
  point1: Point,
  point2: Point
): Point {
  const dist1 = csm.point.distance(intersectionPoint, point1);
  const dist2 = csm.point.distance(intersectionPoint, point2);
  return dist1 > dist2 ? point1 : point2;
}

export default class ExternalAngleTool
  extends BaseAnnotationTool
  implements cst.IBaseAnnotationTool<ExternalAngleAnnotation>
{
  public toolBuilder: ChainingToolBuilder<ExternalAngleAnnotation>;
  public addNewMeasurement: ChainingToolBuilder<ExternalAngleAnnotation>['addNewMeasurement'];

  constructor(props: Partial<{ configuration: Partial<ExternalAngleToolConfiguration> }> = {}) {
    const defaultProps = {
      name: 'ExternalAngleTool',
      supportedInteractionTypes: ['Mouse', 'Touch'] as const,
      svgCursor: cursors.angleCursor,
      configuration: {
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
      } as ExternalAngleToolConfiguration,
    };

    super(props, defaultProps);
    this.toolBuilder = new ChainingToolBuilder({
      name: this.name,
      createNewMeasurement: this.createNewMeasurement,
      handlesKeys: ['l1Start', 'l1End', 'l2Start', 'l2End'],
      maximumToolData: -1,
    });
    this.addNewMeasurement = this.toolBuilder.addNewMeasurement;
  }

  get configuration(): ExternalAngleToolConfiguration {
    return super.configuration as ExternalAngleToolConfiguration;
  }

  createNewMeasurement = (eventData: any): ExternalAngleAnnotation =>
    ({
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      handles: {
        l1Start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
      },
    } as ExternalAngleAnnotation);

  // TODO(REFACTO): All angle and projection could be cached to reduce computation on rerender
  // where tool is not modified.
  updateCachedStats = (
    _image: csc.Image,
    element: HTMLElement,
    data: ExternalAngleAnnotation
  ): void => {};

  pointNearTool = (
    element: HTMLElement,
    data: ExternalAngleAnnotation,
    coords: csc.PixelCoordinate,
    interactionType: 'mouse' | 'touch'
  ) => {
    if (data.visible === false) {
      return false;
    }
    const nearDistance =
      interactionType === 'mouse' ? cst.store.state.clickProximity : cst.store.state.touchProximity;

    const nearHandleInRange = getHandleNearImagePoint(element, data.handles, coords, nearDistance);

    let isNearL1 = false;
    let isNearL2 = false;
    if (!('l2End' in data.handles)) {
      return nearHandleInRange !== undefined;
    }
    if (data.handles.l1End && data.handles.l1Start) {
      isNearL1 =
        lineSegDistance(element, data.handles.l1Start, data.handles.l1End, coords) < nearDistance;

      if (isNearL1) {
        data.handles.l1Start.movesIndependently = false;
        data.handles.l1End.movesIndependently = false;
        if (data.handles?.l2Start) {
          data.handles.l2Start.movesIndependently = true;
        }
        if (data.handles?.l2End) {
          data.handles.l2End.movesIndependently = true;
        }
      }
    }
    if (!isNearL1 && data.handles.l2End && data.handles.l2Start) {
      isNearL2 =
        lineSegDistance(element, data.handles.l2Start, data.handles.l2End, coords) < nearDistance;

      if (isNearL2) {
        data.handles.l2Start.movesIndependently = false;
        data.handles.l2End.movesIndependently = false;
        if (data.handles?.l1Start) {
          data.handles.l1Start.movesIndependently = true;
        }
        if (data.handles?.l1End) {
          data.handles.l1End.movesIndependently = true;
        }
      }
    }

    return nearHandleInRange !== undefined || isNearL1 || isNearL2;
  };

  drawTool = (
    element: HTMLElement,
    ctx: CanvasRenderingContext2D,
    data: ExternalAngleAnnotation
  ): void => {
    // Draw the handles
    const toolColor = cst.toolColors.getToolColor(data);
    const possiblyActiveColor = cst.toolColors.getColorIfActive(data);
    const { handleRadius, drawHandlesOnHover, hideHandlesIfMoving } = this.configuration;

    const handleOptions = {
      handleRadius,
      drawHandlesIfActive: drawHandlesOnHover,
      hideHandlesIfMoving,
    };

    this.drawAngleArc(element, ctx, data);

    // We draw the line after the angle Arc because angle arc also draw a dotted line that we might need to draw over.

    let isMovingIndependently = data.handles.l1Start?.movesIndependently;
    let color = isMovingIndependently ? toolColor : possiblyActiveColor;
    drawHandles(ctx, { element }, _.pick(data.handles, ['l1Start', 'l1End']), {
      ...handleOptions,
      color,
    });
    if (data.handles.l1Start && data.handles.l1End) {
      drawLine(ctx, element, data.handles.l1Start, data.handles.l1End, {
        color,
        lineDash: false,
      });
    }

    isMovingIndependently = data.handles.l2Start?.movesIndependently;
    color = isMovingIndependently ? toolColor : possiblyActiveColor;
    drawHandles(ctx, { element }, _.pick(data.handles, ['l2Start', 'l2End']), {
      ...handleOptions,
      color,
    });
    if (data.handles.l2Start && data.handles.l2End) {
      drawLine(ctx, element, data.handles.l2Start, data.handles.l2End, {
        color,
        lineDash: false,
      });
    }
  };

  renderToolData = (evt: EventWithCornerstoneDetail): void => {
    const eventData = evt.detail;
    const { element } = eventData;
    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = cst.getToolState(evt.currentTarget, this.name);

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);
    draw(context, (ctx: CanvasRenderingContext2D) => {
      toolData?.data.forEach((data: ExternalAngleAnnotation) => {
        this.drawTool(element, ctx, data);
      });
    });
  };

  drawAngleArc = (
    element: HTMLElement,
    ctx: CanvasRenderingContext2D,
    data: ExternalAngleAnnotation
  ): void => {
    const canvasHandles = _.mapValues(data.handles, (handle) =>
      csc.pixelToCanvas(element, handle as csc.PixelCoordinate)
    );
    const intersectionPoint = getLineIntersection(
      { start: canvasHandles.l1Start, end: canvasHandles.l1End },
      { start: canvasHandles.l2Start, end: canvasHandles.l2End }
    ) as csc.CanvasCoordinate;
    if (!intersectionPoint) return;

    const cornerTipProjection1 = projectAlongAxis(
      element,
      csc.canvasToPixel(element, intersectionPoint as csc.CanvasCoordinate),
      csc.canvasToPixel(element, intersectionPoint as csc.CanvasCoordinate),
      csc.canvasToPixel(element, canvasHandles.l1Start as csc.CanvasCoordinate),
      5
    );

    const cornerTipProjection2 = projectAlongAxis(
      element,
      csc.canvasToPixel(element, intersectionPoint as csc.CanvasCoordinate),
      csc.canvasToPixel(element, intersectionPoint as csc.CanvasCoordinate),
      csc.canvasToPixel(element, canvasHandles.l2Start as csc.CanvasCoordinate),
      5
    );

    drawLine(
      ctx,
      element,
      csc.canvasToPixel(element, canvasHandles.l1Start),
      cornerTipProjection1,
      {
        color: LINE_PROJECTION_COLOR,
        lineDash: [10, 5],
      }
    );
    drawLine(
      ctx,
      element,
      csc.canvasToPixel(element, canvasHandles.l2Start),
      cornerTipProjection2,
      {
        color: LINE_PROJECTION_COLOR,
        lineDash: [10, 5],
      }
    );

    // Ensure pointy end of the angle is always drawn
    drawLine(
      ctx,
      element,
      csc.canvasToPixel(element, intersectionPoint as csc.CanvasCoordinate),
      cornerTipProjection1,
      {
        color: LINE_PROJECTION_COLOR,
        lineDash: false,
      }
    );
    drawLine(
      ctx,
      element,
      csc.canvasToPixel(element, intersectionPoint as csc.CanvasCoordinate),
      cornerTipProjection2,
      {
        color: LINE_PROJECTION_COLOR,
        lineDash: false,
      }
    );

    const farthestPoint1 = getFarthestFromIntersection(
      intersectionPoint,
      canvasHandles.l1Start,
      canvasHandles.l1End
    );
    const farthestPoint2 = getFarthestFromIntersection(
      intersectionPoint,
      canvasHandles.l2Start,
      canvasHandles.l2End
    );

    const angle = getLinesAngle(
      [intersectionPoint, farthestPoint1],
      [intersectionPoint, farthestPoint2]
    );

    const l1Angle = getLinesAngle(
      [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
      ],
      [intersectionPoint, farthestPoint1]
    );
    const l2Angle = getLinesAngle(
      [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
      ],
      [intersectionPoint, farthestPoint2]
    );
    const minAngle = Math.min(l1Angle, l2Angle);
    const maxAngle = Math.max(l1Angle, l2Angle);
    let clockwise = true;
    if (maxAngle - minAngle > Math.PI) {
      clockwise = false;
    }
    const arcRadius = 20;

    const midPointAngle = clockwise
      ? (maxAngle + minAngle) / 2
      : (maxAngle + minAngle) / 2 + Math.PI;

    path(ctx, { color: 'red' }, (drawContext: CanvasRenderingContext2D) => {
      drawContext.arc(
        intersectionPoint.x,
        intersectionPoint.y,
        arcRadius,
        minAngle,
        maxAngle,
        !clockwise
      );
    });

    const projectionAxis = rotateVector({ x: 1, y: 0 }, midPointAngle);
    const textAlign =
      midPointAngle > Math.PI / 2 && midPointAngle < (3 * Math.PI) / 2 ? 'right' : 'left';

    const textBoxCoordinates = csc.pixelToCanvas(
      element,
      projectAlongAxis(
        element,
        csc.canvasToPixel(element, intersectionPoint as csc.CanvasCoordinate),
        csc.canvasToPixel(element, { x: 0, y: 0 } as csc.CanvasCoordinate),
        csc.canvasToPixel(element, projectionAxis as csc.CanvasCoordinate),
        arcRadius + 20
      ) as csc.PixelCoordinate
    );

    ctx.save();
    ctx.textAlign = textAlign;
    drawTextBox(
      ctx,
      toStringDegree(Math.abs(angle), 1),
      textBoxCoordinates.x - CORNERSTONE_TEXT_PADDING,
      textBoxCoordinates.y,
      'white',
      { centering: { y: true } }
    );
    ctx.restore();
  };
}
